import { Vector3 } from '@babylonjs/core/Maths/math.vector'
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh'
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh'
import { Mesh } from '@babylonjs/core/Meshes/mesh'
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder'
import isNull from 'lodash/isNull'
import { all, call, takeLatest } from 'redux-saga/effects'

import managers from '../../lib/managers'
import { BabylonManager } from '../../lib/managers/BabylonManager'
import { MeshPoints } from '../../lib/toolBoxes/3D'
import { Workspace } from '../../lib/toolBoxes/3D/tools/workspace/Workspace.tool'
import { updateMeshPoints } from '../../slices/3D'
import { IVector } from '../../types'

export function* createSpheresAndVectors(vectorInput: IVector, manager: BabylonManager, meshPointsTool: MeshPoints) {
    const newVector = new Vector3(vectorInput.x, vectorInput.y, vectorInput.z)
    const newSphere: Mesh = yield call(MeshBuilder.CreateSphere, MeshPoints.MESH_ID, {
        diameter: MeshPoints.MESH_POINTS_DIAMETER,
    })

    newSphere.id = MeshPoints.MESH_ID
    newSphere.position = newVector
    newSphere.parent = manager.rootNode!
    newSphere.material = meshPointsTool.measureHandleMaterial

    return newVector
}

export function shouldDeleteMeshesThatAreMeshPoints(mesh: AbstractMesh) {
    return mesh.id === MeshPoints.MESH_ID
}

export function* handleDrawingMeshPoints(action: ReturnType<typeof updateMeshPoints>) {
    const manager: BabylonManager | null = yield call(managers.get3DManager)

    if (isNull(manager)) return

    if (isNull(manager.rootNode)) return

    const [workspace, meshPoints]: [Workspace, MeshPoints] = yield call(manager.getTools, [
        Workspace.NAME,
        MeshPoints.NAME,
    ])

    yield call(workspace.deleteMeshesInCurrentScene, shouldDeleteMeshesThatAreMeshPoints)

    if (!isNull(action.payload)) {
        const vectorPoints: Vector3[] = yield all(
            action.payload.map(function* (vectorInput) {
                return yield call(createSpheresAndVectors, vectorInput, manager, meshPoints)
            })
        )

        if (vectorPoints.length === 2) {
            const newLineMesh: LinesMesh = yield call(
                MeshBuilder.CreateDashedLines,
                MeshPoints.MESH_ID,
                {
                    points: vectorPoints,
                    dashSize: MeshPoints.MESH_POINTS_DASH_SIZE,
                    gapSize: MeshPoints.MESH_POINTS_DASH_GAP_SIZE,
                    dashNb: MeshPoints.MESH_POINTS_DASH_NUMBER,
                },
                manager.getScene()
            )

            newLineMesh.id = MeshPoints.MESH_ID
            newLineMesh.parent = manager.rootNode!
            newLineMesh.color = MeshPoints.MESH_POINTS_LINE_COLOR
            newLineMesh.edgesColor = MeshPoints.MESH_POINTS_LINE_COLOR.toColor4(1)
            newLineMesh.edgesWidth = MeshPoints.MESH_POINTS_LINE_EDGE_WIDTH
            newLineMesh.enableEdgesRendering()
        }
    }
}

export function* watchForMeasurementPointsChange() {
    yield takeLatest(updateMeshPoints.type, handleDrawingMeshPoints)
}
